// # FILE: src/app/(public)/guard-portal/page.tsx
// # VERSION: 2.0.0
// # PURPOSE: «Оставить отзыв о ЧОП» — честный флоу от работника/охранника о компании.
// #   Работает по РЕАЛЬНЫМ карточкам ЧОП (seo_companies, /chop/[slug]) — отзыв попадает
// #   в модерацию (pending). Фейковая «верификация УЛЧ» (setTimeout имитировал Росгвардию)
// #   УБРАНА (LG-20260831-esia-decision-gate §7 п.5; LG-20260831-inventory 🔴 guard-portal).
// # SCOPE: client component (public, но авторизация не требуется — user_id берётся из сессии при наличии).
// # INPUT: сессия (опциональна), форма отзыва.
// # OUTPUT: отзыв → chop_reviews (status=pending, source=guard_portal).
// # KEYWORDS: [DOMAIN(9): B2C-Passport; CONCEPT(8): Reviews, PDn-Consent; TECH(7): UI]
// # LINKS: READS_DATA_FROM(seo_companies); WRITES_DATA_TO(chop_reviews)
// # Дата: 2026-08-31
// # <MEMENTO>Почему pending + consent: прежняя версия публиковала отзыв (status=published)
// #   сразу и без согласия — риск по 152-ФЗ (author_name = ПДн) и спам/накрутка рейтинга.
// #   УЧО НЕ является верификацией: ввели «проверку» через setTimeout — это имитация
// #   официальной проверки Росгвардии, что вводило в заблуждение. Правильно: УЧО — простое
// #   поле-декларация БЕЗ бейджа «проверен», а личность подтверждается только через ЕСИА.</MEMENTO>

"use client";

import { useState, useEffect, useMemo } from "react";
import Link from "next/link";
import {
  ShieldCheck,
  ArrowRight,
  ArrowLeft,
  Search,
  Loader2,
  Star,
  AlertTriangle,
} from "lucide-react";
import { createClient } from "@oxpa/shared/lib/supabase/client";
import { Button } from "@oxpa/shared/ui/button";
import { Input } from "@oxpa/shared/ui/input";
import { Textarea } from "@oxpa/shared/ui/textarea";
import { useToast } from "@oxpa/shared/hooks/use-toast";

interface SeoCompany {
  id: string;
  name_short: string;
  region: string | null;
  slug: string | null;
}

type Step = "search" | "review" | "success";

export default function GuardPortalPage() {
  const supabase = useMemo(() => createClient(), []);
  const { toast } = useToast();

  const [step, setStep] = useState<Step>("search");
  const [searchTerm, setSearchTerm] = useState("");
  const [chops, setChops] = useState<SeoCompany[]>([]);
  const [selectedChop, setSelectedChop] = useState<SeoCompany | null>(null);

  const [rating, setRating] = useState(0);
  const [authorName, setAuthorName] = useState("");
  const [comment, setComment] = useState("");
  const [anonymized, setAnonymized] = useState(false);
  const [consent, setConsent] = useState(false);
  const [uchoNumber, setUchoNumber] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Поиск по реальным карточкам ЧОП (seo_companies).
  useEffect(() => {
    if (searchTerm.length < 2) return;
    let active = true;
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from("seo_companies")
        .select("id,name_short,region,slug")
        .or(
          `name_short.ilike.%${searchTerm}%,name_full.ilike.%${searchTerm}%`
        )
        .limit(5);
      if (active) setChops(data ?? []);
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [searchTerm, supabase]);

  function handleSelectChop(chop: SeoCompany) {
    setSelectedChop(chop);
    setStep("review");
  }

  async function handleSubmitReview() {
    if (rating === 0) {
      toast({ title: "Ошибка", description: "Пожалуйста, поставьте оценку от 1 до 5", variant: "destructive" });
      return;
    }
    if (!anonymized && !authorName.trim()) {
      toast({ title: "Ошибка", description: "Укажите имя (или включите анонимность)", variant: "destructive" });
      return;
    }
    if (!consent) {
      toast({ title: "Ошибка", description: "Подтвердите согласие на обработку и публикацию отзыва", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);

    // user_id берём из сессии (если охранник залогинен), иначе null.
    const {
      data: { user },
    } = await supabase.auth.getUser();

    const { error } = await supabase.from("chop_reviews").insert({
      chop_id: selectedChop!.id,
      author_name: anonymized ? null : authorName.trim(),
      author_role: "guard",
      user_id: user?.id ?? null,
      is_anonymous: anonymized,
      source: "guard_portal",
      rating,
      comment: comment.trim() || null,
      status: "pending", // модерация перед публикацией
    });

    setIsSubmitting(false);

    if (error) {
      console.error(error);
      toast({ title: "Ошибка", description: "Не удалось отправить отзыв", variant: "destructive" });
    } else {
      setStep("success");
    }
  }

  return (
    <div className="public-portal min-h-screen bg-[hsl(var(--portal-bg))] text-[hsl(var(--portal-text))] font-mono flex flex-col">
      <header className="relative z-[1] border-b border-[hsl(var(--portal-border))] p-6 flex flex-wrap justify-between items-center gap-3 bg-[hsl(var(--portal-bg))]">
        <Link
          href="/guard"
          className="flex items-center gap-2 text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text))] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          <span className="uppercase text-xs tracking-widest font-bold">Паспорт охранника</span>
        </Link>
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[hsl(var(--portal-accent))]" strokeWidth={1.5} />
          <span className="uppercase tracking-widest font-bold text-sm hidden sm:inline">
            Отзыв о ЧОП
          </span>
        </div>
        <Link href="/rating" className="text-xs uppercase tracking-widest text-[hsl(var(--portal-text-muted))] hover:text-[hsl(var(--portal-text))] transition-colors">
          К рейтингу
        </Link>
      </header>

      <main className="relative z-[1] flex-1 flex flex-col items-center justify-center p-6 pb-20 mt-4 h-full">
        <div className="w-full max-w-xl mx-auto">
          {step === "search" && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <ShieldCheck className="w-16 h-16 text-[hsl(var(--portal-accent))] mx-auto mb-4" strokeWidth={1.5} />
                <h1 className="text-2xl sm:text-3xl font-black uppercase mb-2">
                  Отзыв о ЧОП
                </h1>
                <p className="text-[hsl(var(--portal-text-secondary))] font-sans">
                  Поделитесь опытом работы в охранной организации — это влияет на национальный рейтинг.
                </p>
              </div>

              <div className="bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] p-6">
                <label className="block text-xs uppercase tracking-widest text-[hsl(var(--portal-text-muted))] mb-2">
                  <span className="font-mono tabular-nums">1.</span> Найдите компанию
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-5 h-5 text-[hsl(var(--portal-text-dim))]" strokeWidth={1.5} />
                  <Input
                    placeholder="Например: Дельта, Витязь..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-12 rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border))] font-sans focus-visible:ring-0 focus-visible:border-[hsl(var(--portal-accent))]"
                  />
                </div>

                {chops.length > 0 && (
                  <div className="mt-2 border border-[hsl(var(--portal-border))] overflow-hidden divide-y divide-[hsl(var(--portal-border))]">
                    {chops.map((chop) => (
                      <button
                        key={chop.id}
                        onClick={() => handleSelectChop(chop)}
                        className="w-full text-left px-4 py-3 bg-[hsl(var(--portal-bg))] hover:bg-[hsl(var(--portal-surface))] transition-colors flex justify-between items-center group"
                        type="button"
                      >
                        <div>
                          <div className="font-bold">{chop.name_short}</div>
                          <div className="text-xs text-[hsl(var(--portal-text-muted))] font-sans">{chop.region ?? ""}</div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-[hsl(var(--portal-text-dim))] group-hover:text-[hsl(var(--portal-accent))]" strokeWidth={1.5} />
                      </button>
                    ))}
                  </div>
                )}
                {searchTerm.length >= 2 && chops.length === 0 && (
                  <div className="mt-2 text-center py-4 text-[hsl(var(--portal-text-muted))] text-sm font-sans">
                    Компания не найдена. Попробуйте другой запрос.
                  </div>
                )}
              </div>
            </div>
          )}

          {step === "review" && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="bg-[hsl(var(--portal-surface))] border border-[hsl(var(--portal-border))] p-6">
                <div className="flex justify-between items-center mb-6">
                  <label className="block text-xs uppercase tracking-widest text-[hsl(var(--portal-text-muted))]">
                    <span className="font-mono tabular-nums">2.</span> Ваш отзыв
                  </label>
                  <button
                    onClick={() => setStep("search")}
                    className="text-xs text-[hsl(var(--portal-accent))] hover:underline"
                    type="button"
                  >
                    Изменить компанию
                  </button>
                </div>

                <p className="text-sm text-[hsl(var(--portal-text-muted))] font-sans mb-6">
                  Выбрано: <strong className="text-[hsl(var(--portal-text))]">{selectedChop?.name_short}</strong>
                </p>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm text-[hsl(var(--portal-text-secondary))] mb-2">Оценка (<span className="font-mono tabular-nums">1–5</span>)</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setRating(star)}
                          type="button"
                          className={`p-2 transition-transform hover:scale-110 ${star <= rating ? "text-[hsl(var(--portal-accent))]" : "text-[hsl(var(--portal-text-dim))]"}`}
                        >
                          <Star strokeWidth={1.5} className={`w-8 h-8 ${star <= rating ? "fill-[hsl(var(--portal-accent))]" : ""}`} />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-[hsl(var(--portal-text-secondary))] mb-1">Как вас представить?</label>
                    <Input
                      placeholder="Имя (или включите анонимность ниже)"
                      value={authorName}
                      onChange={(e) => setAuthorName(e.target.value)}
                      className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border))] font-sans focus-visible:ring-0 focus-visible:border-[hsl(var(--portal-accent))]"
                      maxLength={60}
                      disabled={anonymized}
                    />
                    <label className="flex items-center gap-2 mt-2 text-xs text-[hsl(var(--portal-text-muted))] font-sans">
                      <input
                        type="checkbox"
                        checked={anonymized}
                        onChange={(e) => setAnonymized(e.target.checked)}
                        className="w-4 h-4 accent-[hsl(var(--portal-accent))]"
                      />
                      Оставить отзыв анонимно
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm text-[hsl(var(--portal-text-secondary))] mb-1">
                      Номер УЧО (необязательно, для справки)
                    </label>
                    <Input
                      placeholder="Например: 77 123456"
                      value={uchoNumber}
                      onChange={(e) => setUchoNumber(e.target.value)}
                      className="rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border))] font-mono tabular-nums focus-visible:ring-0 focus-visible:border-[hsl(var(--portal-accent))]"
                      maxLength={30}
                    />
                    <p className="text-[10px] text-[hsl(var(--portal-text-dim))] font-sans mt-1">
                      Указывается как декларация — OXPA не проверяет охранный статус. Подтвердите у работодателя или в Росгвардии.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm text-[hsl(var(--portal-text-secondary))] mb-1">Расскажите о плюсах и минусах</label>
                    <Textarea
                      placeholder="Зарплата, условия на постах, отношение руководства..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      className="min-h-[120px] rounded-none bg-[hsl(var(--portal-surface))] border-[hsl(var(--portal-border))] font-sans resize-none focus-visible:ring-0 focus-visible:border-[hsl(var(--portal-accent))]"
                    />
                  </div>

                  <label className="flex items-start gap-2 text-xs text-[hsl(var(--portal-text-muted))] font-sans leading-relaxed">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      className="w-4 h-4 accent-[hsl(var(--portal-accent))] mt-0.5"
                    />
                    <span>
                      Согласен на обработку и публикацию отзыва. Отзыв появится после модерации.
                    </span>
                  </label>

                  <Button
                    onClick={handleSubmitReview}
                    disabled={isSubmitting || rating === 0 || !consent}
                    className="w-full h-12 rounded-none bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] font-black uppercase tracking-widest shadow-[4px_4px_0_hsl(var(--portal-accent-hover))] disabled:opacity-50 disabled:shadow-none"
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Отправить отзыв"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {step === "success" && (
            <div className="text-center space-y-6 animate-in zoom-in duration-500">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-[hsl(var(--portal-accent-muted))] border-2 border-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent))] mb-4">
                <ShieldCheck className="w-12 h-12" strokeWidth={1.5} />
              </div>
              <h2 className="text-3xl font-black uppercase">Отзыв отправлен</h2>
              <p className="text-[hsl(var(--portal-text-secondary))] font-sans max-w-md mx-auto">
                Спасибо! Отзыв уйдёт на модерацию и после проверки появится в карточке компании.
              </p>
              <div className="pt-4 flex gap-3 justify-center flex-wrap">
                <Link href="/rating">
                  <Button className="h-12 px-8 rounded-none bg-[hsl(var(--portal-accent))] text-[hsl(var(--portal-accent-on))] hover:bg-[hsl(var(--portal-accent-hover))] font-bold uppercase tracking-wider shadow-[4px_4px_0_hsl(var(--portal-accent-hover))]">
                    К рейтингу
                  </Button>
                </Link>
                <Link href="/guard">
                  <Button variant="outline" className="h-12 px-8 rounded-none border-[hsl(var(--portal-border))] text-[hsl(var(--portal-text-secondary))] hover:bg-[hsl(var(--portal-surface))] hover:border-[hsl(var(--portal-accent))] font-bold uppercase tracking-wider bg-transparent">
                    Паспорт охранника
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {step === "search" && (
            <div className="mt-8 text-center">
              <AlertTriangle className="inline-block w-4 h-4 text-[hsl(var(--portal-text-dim))] mr-1" strokeWidth={1.5} />
              <span className="text-[10px] text-[hsl(var(--portal-text-dim))] font-sans">
                Отзыв публикуется после модерации. Не указывайте персональные данные третьих лиц.
              </span>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}